import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { TrialStatus } from '@/reducers/auth/userReducer'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'
import { fetchBillingPortalSession } from '@/actions/settings/billingActions'
import {
  DOWNLOAD_ACCOUNT_ZIP_KEY,
  downloadAccountInformation,
  fetchCurrentUserDetailsIfNeeded,
} from '@/actions/userActions'
import {
  getCurrentUser,
  getFreeTrialStatus,
  hasManualRequest,
  isCancelling,
  isFreeTrialPromoCode,
} from '@/selectors/user.selectors'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  CancellationStepIdentifier,
  createAutomaticCancellationAnalyticsRecord,
} from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.slice'
import { useGetSaveMoneyAndTimePageData } from '@/features/user/settings/sections/self-serve-cancellation/lib/helpers'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import {
  isEligibleForDiscount,
  selectCanSelfServeCancel,
} from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import {
  fetchUserTransactions,
  USER_TRANSACTION_KEY,
} from '@/features/transactions/transactions.slice'
import {
  fetchUserTaxEstimates,
  ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY,
} from '@/features/taxes/quarterly-tax-estimates/userTaxEstimates.slice'

import { Button, Card, Heading, HeadingVariants } from '@/ui'
import { ConfirmManualCancellationDialog } from './ConfirmCancellationDialog'
import { AccountDownloadDialog } from './AccountDownloadDialog'
import { ReactivatePlanDialog } from './ReactivatePlanDialog'
import { InvoiceList } from './InvoiceList'
import { BillingTable } from './BillingTable'

// TODO: Migrate to V2 skipcq: JS-0099
import { selectCurrentAnnualTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilingForms.slice'
import { fetchPayrollProfileIfNeeded } from '@/featuresV1/Payroll/payrollActions'

export const PlansAndBillingCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const membershipIsCancelling = useReselector(isCancelling)
  const membershipHasManualRequest = useReselector(hasManualRequest)
  const currentUser = useReselector(getCurrentUser)
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false)
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)
  const data = useGetSaveMoneyAndTimePageData()

  const downloadingFiles = useReselector(selectIsFetchingForKeys, [
    DOWNLOAD_ACCOUNT_ZIP_KEY,
  ])

  const isLoadingTransactionsAndUserTaxEstimates = useReselector(
    selectIsFetchingForKeys,
    [USER_TRANSACTION_KEY, ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY]
  )

  const isInActiveFreeTrial =
    hasFreeTrialPromoCode && userFreeTrialStatus === TrialStatus.active

  useEffect(() => {
    pageView('plans and billing page')
  }, [pageView])

  const redirectToStripePortal = useCallback(async () => {
    const res = await fetchBillingPortalSession()(dispatch)
    track('manage stripe plan')

    if (res) {
      window.location.href = res.url
    }
  }, [dispatch, track])

  const downloadZipFile = async () => {
    const result = await downloadAccountInformation(currentUser?.id)(dispatch)
    track('download account zip file')

    if (result) {
      setDownloadDialogOpen(true)
    }
  }

  const isSelfServeCancel = useReselector(
    selectCanSelfServeCancel,
    currentTaxYear
  )

  const enableDiscounts = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableCancellationDiscounts,
    false
  )

  const eligibleForDiscount = useReselector(
    isEligibleForDiscount,
    enableDiscounts
  )

  const saveFlowCancellationAction = useCallback(async () => {
    const record = await createAutomaticCancellationAnalyticsRecord({
      finalStepReached: CancellationStepIdentifier.SAVING_TIME_AND_MONEY,
      additionalDetails: {
        hasPersonalizedSavingsPage: data.showPersonalizedPage,
        financialAccountConnectionAvailable: data.faAccountAlertType,
        discountAvailable: eligibleForDiscount,
      },
    })(dispatch)

    navigate('/user-cancellation', { state: { recordId: record?.id } })
  }, [dispatch, navigate, data, eligibleForDiscount])

  const handleSelfServeCancellation = () => {
    saveFlowCancellationAction()
    sendUserCancellationMessageZap(currentUser, 'clicked_canceled')
    track('click cancel plan')
  }

  useEffect(() => {
    dispatch(fetchSubscriptions())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserTransactions())
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
  }, [dispatch])

  return (
    <Card className="flex flex-col gap-4">
      <Heading variant={HeadingVariants.H2}>Plan & Billing</Heading>
      <BillingTable />

      {isInActiveFreeTrial ? (
        <div id="reactivation">
          <Button
            variant="primary"
            onClick={() => {
              track('click purchase plan')
              navigate('/accounts/choose-plan')
            }}
          >
            Purchase a Heard Plan
          </Button>
        </div>
      ) : (
        <>
          {membershipIsCancelling && (
            <div id="reactivation">
              <ReactivatePlanDialog />

              <Button
                variant="secondary"
                onClick={downloadZipFile}
                loading={downloadingFiles}
                disabled={downloadingFiles}
              >
                Download Account Information
              </Button>

              <AccountDownloadDialog
                data-state={downloadDialogOpen ? 'open' : 'closed'}
              />
            </div>
          )}

          {!membershipIsCancelling && (
            <div>
              <Button variant="secondary" onClick={redirectToStripePortal}>
                Change Plan
              </Button>

              <div>
                {isSelfServeCancel && (
                  <Button
                    variant="link"
                    onClick={() => {
                      handleSelfServeCancellation()
                    }}
                    disabled={isLoadingTransactionsAndUserTaxEstimates}
                  >
                    Cancel Plan
                  </Button>
                )}

                {!isSelfServeCancel && !membershipHasManualRequest && (
                  <ConfirmManualCancellationDialog />
                )}
              </div>
            </div>
          )}

          <InvoiceList />
        </>
      )}
    </Card>
  )
}
